import type React from "react";
import { useRef } from "react";
import { useEventListener } from "@litres-libs/global-hooks";
import { hasWindow } from "@litres/utils";

interface IScrollPosition {
  x: number;
  y: number;
}

interface IGetScrollPosProps {
  element?: React.RefObject<HTMLElement> | null;
  useWindow: boolean;
}

export interface IPrevAndCurrScrollPos {
  prevPos: IScrollPosition;
  currPos: IScrollPosition;
}

const getScrollPosition = (props: IGetScrollPosProps): IScrollPosition => {
  const defaultPos = { x: 0, y: 0 };

  if (!hasWindow) {
    return defaultPos;
  }

  const { element, useWindow } = props;
  const target = element ? element.current : document.body;
  const position = target?.getBoundingClientRect();

  if (!position) {
    return defaultPos;
  }

  return useWindow
    ? { x: window.scrollX, y: window.scrollY }
    : { x: position.left, y: position.top };
};

export const useScrollPosition = (
  effect: (prop: IPrevAndCurrScrollPos) => void,
  element: React.RefObject<HTMLElement> | null,
  useWindow: boolean,
  wait: number
): void => {
  const position = useRef(getScrollPosition({ useWindow }));

  let throttleTimeout: NodeJS.Timeout | null = null;

  const callBack = (): void => {
    const currPos = getScrollPosition({ element, useWindow });
    effect({ prevPos: position.current, currPos });
    position.current = currPos;
    throttleTimeout = null;
  };

  const handleScroll = (): void => {
    if (wait) {
      if (throttleTimeout === null) {
        throttleTimeout = setTimeout(callBack, wait);
      }
    } else {
      callBack();
    }
  };

  useEventListener("scroll", handleScroll);
};
